import { fromZonedTime } from 'date-fns-tz';
import type { Ref } from 'react';
import { useMemo } from 'react';
import { getBrowserLanguage } from '../../common-functions';
import type { DataGridDataCellBaseProps } from '../data-grid-types';
import TruncatedCell from './truncated-cell.component';

const BROWSER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;
const BROWSER_LANGUAGE = getBrowserLanguage();

interface DataGridDataDateCellProps extends DataGridDataCellBaseProps {
  ref?: Ref<HTMLDivElement>;
  dateOnly?: boolean;
  timeOnly?: boolean;
  /** Optional formatter for date-only values. When provided and dateOnly is true, this function is used instead of toLocaleDateString. */
  formatDate?: (date: Date) => string;
}

function DataGridDataDateCell({
  ref,
  value,
  dateOnly = false,
  timeOnly = false,
  formatDate,
}: DataGridDataDateCellProps) {
  const cellValue = useMemo(() => {
    if (!value || (typeof value !== 'string' && !(value instanceof Date))) return '';
    if (timeOnly) return String(value);

    const utcDate = fromZonedTime(value, BROWSER_TIMEZONE);
    if (Number.isNaN(utcDate.getTime())) return '-';

    if (dateOnly) {
      return formatDate ? formatDate(utcDate) : utcDate.toLocaleDateString(BROWSER_LANGUAGE);
    }
    return utcDate.toLocaleString(BROWSER_LANGUAGE);
  }, [value, dateOnly, timeOnly, formatDate]);

  return <TruncatedCell ref={ref}>{cellValue}</TruncatedCell>;
}

export default DataGridDataDateCell;
